import { Roles } from '@consts/role';
import { AdminUser, User } from '@models/auth';
import { Membership } from '@models/membership';
import { ProductModel } from '@models/product';
import { resolveMembershipDiscount } from './membership-discount';

export const isAdmin = (roles?: Roles[]) => {
  return (roles || []).includes(Roles.ADMIN);
};

export const isPartnerRole = (user?: User | AdminUser | null) => {
  if (
    user?.roles?.includes(Roles.SELLER) ||
    user?.roles?.includes(Roles.TUTOR) ||
    user?.roles?.includes(Roles.EDITOR) ||
    user?.roles?.includes(Roles.PARTNER) ||
    user?.roles?.includes(Roles.ADMIN) ||
    user?.roles?.includes(Roles.MEMBERSHIP_PARTNER) ||
    user?.roles?.includes(Roles.INSTRUCTOR)
  ) {
    return true;
  }

  return false;
};

export const discountedAmountBy = (amount: number, discountPercent: number) => {
  const result = ((amount || 0) * (100 - (discountPercent || 0))) / 100;
  return discountPercent > 0 ? Math.floor(result / 10) * 10 : result;
};

export const getAppliedMembership = (product?: ProductModel, userMembership?: Partial<Membership>) => {
  const productMemberships: Partial<Membership>[] = product?.memberships || [];
  const productId = product?._id;
  const appliedMembership = productMemberships.find((membership) => {
    return membership.alias === userMembership?.alias && userMembership?.qualified;
  });

  if (!appliedMembership) {
    return appliedMembership;
  }

  const resolution = resolveMembershipDiscount(appliedMembership, productId);
  return {
    ...appliedMembership,
    discountPercent: resolution.percent,
  };
};

const readyForGPS = async () => {
  return new Promise((resolve) => {
    const watchId = navigator.geolocation.watchPosition(
      () => {
        navigator.geolocation.clearWatch(watchId);
        resolve(true);
      },
      () => {
        resolve(false);
      },
      { enableHighAccuracy: true },
    );
  });
};

export const getCurrentLocation = async () => {
  const isReady = await readyForGPS();
  const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

  if (permissionStatus.state === 'denied') {
    window.alert('브라우저 설정에서 위치정보 접근을 허용해주세요.');
  }
  if (isReady) {
    return new Promise<{ err: number; latitude: number; longitude: number; time?: string }>((resolve, reject) => {
      if (navigator.geolocation) {
        const now = new Date();
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              err: 0,
              time: now.toLocaleTimeString(),
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (err) => {
            console.log('getCurrentLocation error', err);
            resolve({
              err: -1,
              latitude: -1,
              longitude: -1,
            });
          },
          { enableHighAccuracy: true, maximumAge: 2000, timeout: 5000 },
        );
      } else {
        reject({ err: -2, latitude: -1, longitude: -1 });
      }
    });
  } else {
    return { err: -3, latitude: -1, longitude: -1 };
  }
};

export const getDistanceBetweenGpsCoordinates = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3; // metres
  const φ1 = (lat1 * Math.PI) / 180; // φ, λ in radians
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const d = R * c; // in metres
  return d;
};

export const clearEmptyKeysFromObject = (obj: object) => {
  const shallowCopyObject = { ...obj };

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'undefined') delete shallowCopyObject[key as keyof typeof shallowCopyObject];
  }

  return shallowCopyObject;
};
